import type { Schemas } from '@ers/sdk';
import { Eye } from 'lucide-react';
import { lazy, Suspense } from 'react';
import type { TaskAlert, TaskExtProps } from '../../app/modules';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { todayISO } from '../../lib/dates';
import { attentionLabel, UNIT_HEALTH } from './labels';

/**
 * Управлінський шар задачі (task.ext.mgmt, контракт MgmtTaskExt): для управлінського кола в межах області дії —
 * одиниця, мітки, «на контролі», коментарі кола, запити звіту, причини уваги; для учасників одиниці поза колом —
 * лише рівень, код і статус. Значки й колонка — легкі (у переліках), розділ картки завантажується ліниво.
 */
export type MgmtExt = Schemas['MgmtTaskExt'];

export function asMgmt(ext: unknown): MgmtExt | null {
  return ext && typeof ext === 'object' ? (ext as MgmtExt) : null;
}

const Panel = lazy(() => import('./TaskMgmtPanel'));

/** Розділ «Управління» в картці задачі. */
export function MgmtTaskPanel(props: TaskExtProps) {
  return (
    <Suspense
      fallback={
        <div role="status" aria-label="Завантаження управлінського шару" className="flex flex-col gap-2">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-3 w-64" />
        </div>
      }
    >
      <Panel {...props} />
    </Suspense>
  );
}

/** Значки в рядку й на картці: код одиниці («P3.2»), стан, «на контролі» з настанням дати, увага. */
export function MgmtBadges({ ext, variant }: TaskExtProps & { variant: 'row' | 'card' }) {
  const m = asMgmt(ext);
  if (!m) return null;
  const unit = m.unit;
  const health = unit?.health ? UNIT_HEALTH[unit.health] : undefined;
  const linked = !unit && m.linkedUnits?.length ? m.linkedUnits[0] : undefined;
  const watchDue = m.watch && m.watch.checkOn <= todayISO();
  return (
    <>
      {unit && (
        <Badge tone={health && unit.health !== 'ok' ? health.tone : 'neutral'} className="key" title={`${unit.levelName} ${unit.code}${health ? ` · ${health.label}` : ''}`}>
          {unit.code}
        </Badge>
      )}
      {linked && (
        <Badge className="key" title={`Підпорядковано одиниці ${linked.code}`}>
          {linked.code}
        </Badge>
      )}
      {watchDue && (
        <Badge tone="info" icon={<Eye className="size-3" aria-hidden />} title={m.watch?.note ?? 'Настала дата контролю'}>
          {variant === 'card' ? 'Контроль' : 'На контролі'}
        </Badge>
      )}
    </>
  );
}

/** Попередження для картки дошки: причина уваги управлінського кола (прострочено, заблоковано, ризик). */
export function mgmtTaskAlerts({ ext }: TaskExtProps): TaskAlert[] {
  const m = asMgmt(ext);
  if (!m || m.unit?.health) return [];
  const urgent = (m.attention ?? []).filter((r) => r === 'overdue' || r === 'blocked' || r === 'at_risk');
  if (!urgent.length) return [];
  const top = attentionLabel(urgent[0]!);
  return [{ key: `mgmt-${urgent[0]}`, tone: top.tone === 'danger' ? 'danger' : 'warning', label: top.label, detail: 'Позначка управлінського кола' }];
}

/** Колонка «Одиниця» подання «Список». */
export function MgmtUnitCell({ ext }: TaskExtProps) {
  const m = asMgmt(ext);
  const code = m?.unit?.code ?? m?.linkedUnits?.[0]?.code;
  return code ? <span className="key">{code}</span> : <span className="text-fg-3">Немає</span>;
}
